import { useEffect, useRef, useState } from 'react'

/**
 * AnimatedNumber — count-up animation for stat values.
 *
 * Mirrors the marketing-site "digits cascade into place" vibe:
 *   - On mount, eases from 0 up to `value` over `duration` ms using an
 *     ease-out cubic curve (fast first, settles gently — matches the
 *     marketing site's word-slide feel)
 *   - Re-animates from the *old* value to the new one whenever `value`
 *     changes (stats refresh every 30s; the tick reads as "live data")
 *   - Respects `prefers-reduced-motion` — no animation, just the final value
 *   - Preserves formatting via a caller-supplied `format` fn (percent,
 *     thousands separator, decimals, etc.)
 *
 * Intentionally does NOT do per-digit odometer rolling — that's a much
 * heavier effect and this count-up reads smoother on dashboard tiles.
 */

type AnimatedNumberProps = {
  readonly value: number
  /** ms of animation. Default 900 feels right for 2-4 digit stats. */
  readonly duration?: number
  /** Format fn called on the intermediate value during the tween. */
  readonly format?: (value: number) => string
  /** Optional className for the wrapping span. */
  readonly className?: string
}

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
}

export function AnimatedNumber({
  value,
  duration = 900,
  format = (n) => String(Math.round(n)),
  className,
}: AnimatedNumberProps) {
  // Start the displayed value at 0 so the first mount tween is actually
  // visible — rendering `value` on the initial paint means the tween has
  // nothing to do and the animation is effectively skipped.
  const [display, setDisplay] = useState<number>(0)
  // Track the last animated-to value so `value` prop changes tween from
  // the current on-screen number (not from 0 every time).
  const fromRef = useRef<number>(0)
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    if (prefersReducedMotion()) {
      setDisplay(value)
      fromRef.current = value
      return
    }

    const start = performance.now()
    const from = fromRef.current
    const to = value
    const delta = to - from

    // Already there — nothing to do
    if (delta === 0) {
      setDisplay(value)
      return
    }

    function tick(now: number) {
      const elapsed = now - start
      const t = Math.min(1, elapsed / duration)
      const eased = easeOutCubic(t)
      const current = from + delta * eased
      setDisplay(current)

      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        fromRef.current = to
        rafRef.current = null
      }
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    }
  }, [value, duration])

  return (
    <span className={className} aria-label={format(value)}>
      {format(display)}
    </span>
  )
}
